import type { CatalogItem as CatalogItemApi } from "@albertine/types/openapi/lmt";
import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router";
import type { CatalogItem } from "../../../common/CatalogItem";
import type { Booking } from "../../../common/types/Booking";
import type { Request } from "../../../common/types/Request";
import { excludeUndefined } from "../../../common/utils/array.util";
import { cdnImage } from "../../../common/utils/image.util";
import { singularPlural } from "../../../common/utils/time.formatted.util";
import { EventEmitter, type EventListener } from "../../../EventEmitter";
import routes from "../../../utils/routes";
import { scrollRefToTop } from "../../../utils/window.util";
import Asset from "../../Common/Asset";
import Button from "../../Common/Buttons";
import { Flex, FlexInline } from "../../Common/Flex";
import Icon from "../../Common/Icon";
import Label from "../../Common/Label";
import type { BucketProps } from "../../Common/Overlay/FullScreenOverlay";
import Tag from "../../Common/Tag";
import ImageBalloons from "./ImageBalloons";
import "./Cart.css";
import { categoryDisplayName } from "../../../common/types/OfferingCategory";

function BookingItemCard(props: {
    item: Booking;
    onClick: (catalogItemId: string) => void;
}) {
    const { item, onClick } = props;
    const isBookingFromCatalog = item.catalogItem;

    return (
        <Flex.Row
            gap="large"
            alignItems="start"
            className={classNames("cart-item", {
                "cart-item--exists-in-catalog": isBookingFromCatalog,
            })}
            onClick={() => (item.catalogItem ? onClick(item.catalogItem) : () => {})}
        >
            <div className="cart-item__thumbnail">
                {item.images && item.images[0] && (
                    <Asset
                        src={cdnImage(item.images[0].url, {
                            height: 64,
                            width: 64,
                        })}
                        className="cart-item__image"
                    />
                )}
            </div>
            <Flex.Column
                gap="small"
                justifyContent="start"
                className="cart-item__details"
            >
                <h4>{item.title}</h4>

                <FlexInline gap="medium" alignSelf="start">
                    {!isBookingFromCatalog && (
                        <Tag.Small.Bold.T90>One-off booking</Tag.Small.Bold.T90>
                    )}

                    {item.offeringCategory && (
                        <Label.Medium.Light.N50>
                            {categoryDisplayName(item.offeringCategory, "en")}
                        </Label.Medium.Light.N50>
                    )}
                </FlexInline>

                <Label.Small>{item.address}</Label.Small>
            </Flex.Column>
        </Flex.Row>
    );
}

function CatalogItemCard(props: {
    item: CatalogItemApi;
    onClick: (catalogItemId: string) => void;
}) {
    const { item, onClick } = props;
    return (
        <Flex.Row
            gap="large"
            alignItems="start"
            className="cart-item cart-item--exists-in-catalog"
            onClick={() => {
                onClick(item.lmtId);
            }}
        >
            <div className="cart-item__thumbnail" />

            <Flex.Column
                gap="small"
                justifyContent="start"
                className="cart-item__details"
            >
                <h4>{item.name}</h4>
                <Label.Medium.Light.N50>
                    {item.offeringCategories
                        .map((category) => categoryDisplayName(category, "en"))
                        .join(", ")}
                </Label.Medium.Light.N50>
                <Label.Small>{item.address}</Label.Small>
            </Flex.Column>
        </Flex.Row>
    );
}

function CartOnMaximise({
    request,
    bookings,
    catalogItems,
    minimize,
}: {
    request: Request;
    bookings: Booking[];
    catalogItems: CatalogItemApi[];
    minimize: () => void;
}) {
    const scrollElementRef = useRef<HTMLDivElement>(null);
    const history = useHistory();

    function goToViewMode(catalogItemId: string) {
        history.push(
            routes.catalogSearch("service", request.id, undefined, catalogItemId),
        );
    }

    useEffect(() => {
        scrollRefToTop(scrollElementRef);
    }, []);

    const totalItems = bookings.length + catalogItems.length;

    return (
        <Flex.Column alignItems="start">
            <Flex.Row alignItems="start" gap="large" className="cart-header">
                <Flex.Column gap="small">
                    <Label.Medium.N10>{request.title}</Label.Medium.N10>
                    <Label.Small.N50>
                        {singularPlural(
                            `${totalItems} option added`,
                            `${totalItems} options added`,
                            totalItems,
                        )}
                    </Label.Small.N50>
                </Flex.Column>
                <Button.Ghost onClick={minimize}>
                    <Icon.Close.Medium />
                </Button.Ghost>
            </Flex.Row>
            <Flex.Column
                alignItems="start"
                className="cart-items"
                ref={scrollElementRef}
            >
                {catalogItems.map((item) => (
                    <CatalogItemCard
                        item={item}
                        key={item.id}
                        onClick={(catalogItemId) => {
                            goToViewMode(catalogItemId);
                        }}
                    />
                ))}
                {bookings.map((item) => (
                    <BookingItemCard
                        item={item}
                        key={item.id}
                        onClick={(catalogItemId) => {
                            goToViewMode(catalogItemId);
                        }}
                    />
                ))}
            </Flex.Column>
        </Flex.Column>
    );
}

function CartOnMinimize({
    request,
    bookings,
    catalogItems,
    maximize,
}: {
    request: Request;
    bookings: Booking[];
    catalogItems: CatalogItemApi[];
    maximize: () => void;
}) {
    const images = [
        ...catalogItems.map((item) => ({
            id: item.id,
            url: item.images ? item.images[0].url : undefined,
        })),
        ...bookings.map((item) => ({
            id: item.id,
            url: item.imageURL,
        })),
    ].filter(excludeUndefined);

    const totalItems = bookings.length + catalogItems.length;

    return (
        <Flex.Column
            className="cart-minimize"
            alignItems="start"
            gap="large"
            onClick={maximize}
        >
            <ImageBalloons images={images} showMax={5} />
            <Flex.Column alignItems="start" gap="small">
                <Label.Medium.N10>{request.title}</Label.Medium.N10>
                <Label.Small.N50>
                    {singularPlural(
                        `${totalItems} option added`,
                        `${totalItems} options added`,
                        totalItems,
                    )}
                </Label.Small.N50>
            </Flex.Column>
        </Flex.Column>
    );
}

type Props = {
    request: Request;
    bookings: Booking[];
} & BucketProps;

export default function Cart({ bookings, request, size, maximize, minimize }: Props) {
    const [catalogItems, setCatalogItems] = React.useState<CatalogItemApi[]>([]);

    useEffect(() => {
        const addCartItemListener: EventListener = {
            name: "addCatalogCartItem",
            listener: (params) => {
                setCatalogItems((prev) => [...prev, params.item]);
            },
        };

        EventEmitter.addListener(addCartItemListener);

        return () => {
            EventEmitter.removeListener(addCartItemListener);
        };
    }, []);

    // if the bookings are updated, remove the same items from the catalogItems if exists
    useEffect(() => {
        const bookingItemIds = bookings.map((booking) => booking.catalogItem);
        setCatalogItems((prev) =>
            prev.filter(
                (item) =>
                    !bookingItemIds.includes(item.id) &&
                    !bookingItemIds.includes(item.lmtId),
            ),
        );
    }, [bookings]);

    const totalItems = bookings.length + catalogItems.length;

    if (totalItems === 0) return null;

    if (size === "minimized") {
        return <CartOnMinimize {...{ request, bookings, catalogItems, maximize }} />;
    }

    return <CartOnMaximise {...{ request, bookings, catalogItems, minimize }} />;
}
